import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Menu, X, LogIn, LogOut, LayoutDashboard, Inbox as InboxIcon, Pencil, ChevronDown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSiteData } from '@/context/SiteDataContext';
import { useEditMode } from '@/context/EditModeContext';
import api from '@/lib/api';

const BASE_NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/leadership', label: 'Leadership' },
  { to: '/departments', label: 'Departments' },
  { to: '/districts', label: 'Districts' },
  { to: '/events', label: 'Events' },
  { to: '/calendar', label: 'Calendar' },
  { to: '/news', label: 'News' },
  { to: '/media', label: 'Media' },
  { to: '/locator', label: 'Churches' },
  { to: '/contact', label: 'Contact' },
];

// Groups top-level nav items with their children (items whose parent_id matches).
// Falls back to BASE_NAV when the admin nav collection is empty.
function useGroupedNav() {
  const { nav } = useSiteData();
  return useMemo(() => {
    const source = (nav && nav.length)
      ? nav.filter(n => n.visible !== false)
      : BASE_NAV.map(b => ({ id: b.to, label: b.label, href: b.to, parent_id: '' }));
    const sorted = [...source].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    const roots = sorted.filter(n => !n.parent_id);
    return roots.map(r => ({
      ...r,
      children: sorted.filter(n => n.parent_id === r.id),
    }));
  }, [nav]);
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [unread, setUnread] = useState(0);
  const { user, logout } = useAuth();
  const { settings } = useSiteData();
  const { editMode, canEdit, toggle: toggleEdit } = useEditMode();
  const navigate = useNavigate();
  const ELEVATED = ['admin', 'prelate', 'bishop', 'secretary', 'cfo', 'dept_leader'];
  const NAV = useGroupedNav();

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const load = () => api.get('/messages/unread-count').then(r => setUnread(r.data.count || 0)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user]);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
            <img
              src={settings?.logo_url || 'https://customer-assets-lqy194kg.emergentagent.net/job_first-ej/artifacts/mmnl3841_WIFI%20WEB%20LOGO.webp'}
              alt="Wisconsin First Jurisdiction seal"
              className="w-12 h-12 object-contain shrink-0"
            />
            <div className="hidden md:block leading-tight whitespace-nowrap">
              <div className="font-serif text-royal text-lg font-bold">{settings?.site_name?.split(' ').slice(0, 2).join(' ') || 'Wisconsin First'}</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-charcoal-muted">Ecclesiastical Jurisdiction · COGIC</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              if (!hasChildren) {
                return (
                  <NavLink
                    key={item.id || item.href || item.to}
                    to={item.href || item.to}
                    data-testid={`nav-${(item.label || '').toLowerCase()}`}
                    className={({ isActive }) =>
                      `px-2 py-2 text-sm font-medium link-hover whitespace-nowrap ${isActive ? 'text-gold' : 'text-charcoal'}`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              }
              const isOpen = openDropdown === item.id;
              return (
                <div
                  key={item.id}
                  className="relative"
                  onMouseEnter={() => setOpenDropdown(item.id)}
                  onMouseLeave={() => setOpenDropdown(cur => cur === item.id ? null : cur)}
                >
                  <button
                    onClick={() => {
                      if (isOpen) navigate(item.href || item.to || '/');
                      else setOpenDropdown(item.id);
                    }}
                    data-testid={`nav-${(item.label || '').toLowerCase()}`}
                    aria-expanded={isOpen}
                    className="px-2 py-2 text-sm font-medium link-hover whitespace-nowrap text-charcoal flex items-center gap-0.5"
                  >
                    {item.label}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`nav-dropdown-${(item.label || '').toLowerCase()}`}
                      className="absolute left-0 top-full pt-1 w-64 z-40"
                    >
                      <div className="bg-white border border-gray-200 rounded-lg shadow-2xl py-2">
                        {item.show_parent_in_dropdown !== false && (
                          <>
                            <NavLink
                              to={item.href || '/'}
                              onClick={() => setOpenDropdown(null)}
                              data-testid={`nav-parent-link-${(item.label || '').toLowerCase()}`}
                              className="block px-4 py-2 text-sm font-semibold text-royal hover:bg-gold/10"
                            >
                              {item.dropdown_label || item.label}
                            </NavLink>
                            <div className="border-t my-1" />
                          </>
                        )}
                        {item.children.map(child => (
                          <NavLink
                            key={child.id || child.href}
                            to={child.href}
                            onClick={() => setOpenDropdown(null)}
                            data-testid={`nav-child-${(child.label || '').toLowerCase().replace(/\W+/g, '-')}`}
                            className={({ isActive }) =>
                              `block px-4 py-2 text-sm ${isActive ? 'text-gold font-semibold' : 'text-charcoal'} hover:bg-gold/10`
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {((settings?.give_href || '/giving').startsWith('http')) ? (
              <a href={settings.give_href} target="_blank" rel="noopener noreferrer"
                 data-testid="header-give-btn" className="hidden md:inline-flex btn-gold text-sm py-2.5 px-5">
                {settings?.give_label || 'Give'}
              </a>
            ) : (
              <Link to={settings?.give_href || '/giving'} data-testid="header-give-btn"
                    className="hidden md:inline-flex btn-gold text-sm py-2.5 px-5">
                {settings?.give_label || 'Give'}
              </Link>
            )}
            {user ? (
              <div className="hidden md:flex items-center gap-2">
                <button onClick={() => navigate('/inbox')} data-testid="header-inbox-btn" className="relative text-sm text-royal hover:text-gold flex items-center gap-1 whitespace-nowrap">
                  <InboxIcon className="w-4 h-4" /> Inbox
                  {unread > 0 && <span className="absolute -top-1 -right-2 bg-gold text-royal-dark text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{unread}</span>}
                </button>
                {canEdit && (
                  <button onClick={toggleEdit} data-testid="header-edit-toggle" title="Toggle visual edit mode"
                    className={`text-sm flex items-center gap-1 whitespace-nowrap px-2 py-1 rounded-full border ${editMode ? 'bg-gold text-royal-dark border-gold' : 'text-royal border-transparent hover:text-gold'}`}>
                    <Pencil className="w-4 h-4" /> {editMode ? 'Editing' : 'Edit'}
                  </button>
                )}
                {ELEVATED.includes(user.role) && (
                  <button onClick={() => navigate('/admin')} data-testid="header-admin-btn" className="text-sm text-royal hover:text-gold flex items-center gap-1 whitespace-nowrap">
                    <LayoutDashboard className="w-4 h-4" /> Admin
                  </button>
                )}
                <button onClick={logout} data-testid="header-logout-btn" className="text-sm text-charcoal-muted hover:text-royal flex items-center gap-1">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            ) : (
              <Link to="/login" data-testid="header-login-btn" className="hidden md:inline-flex items-center gap-1 text-sm text-royal hover:text-gold whitespace-nowrap">
                <LogIn className="w-4 h-4" /> Sign In
              </Link>
            )}
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="xl:hidden p-2 text-royal">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      {open && (
        <div data-testid="mobile-nav" className="lg:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 space-y-1">
            {NAV.map((item) => (
              <div key={item.id || item.href}>
                <NavLink
                  to={item.href || item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${(item.label || '').toLowerCase()}`}
                  className={({ isActive }) => `block px-3 py-3 rounded-md text-base font-medium ${isActive ? 'bg-royal text-white' : 'text-charcoal hover:bg-gray-50'}`}
                >
                  {item.label}
                </NavLink>
                {item.children && item.children.length > 0 && (
                  <div className="ml-4 border-l border-gold/40 pl-3 space-y-0.5 mb-2">
                    {item.children.map(child => (
                      <NavLink
                        key={child.id || child.href}
                        to={child.href}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-child-${(child.label || '').toLowerCase().replace(/\W+/g, '-')}`}
                        className={({ isActive }) => `block px-2 py-1.5 rounded text-sm ${isActive ? 'text-gold font-semibold' : 'text-charcoal-muted'} hover:bg-gray-50`}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to={settings?.give_href || '/giving'} onClick={() => setOpen(false)} className="block mt-4 btn-gold text-center">{settings?.give_label || 'Give'}</Link>
            {user ? (
              <button onClick={() => { logout(); setOpen(false); }} className="w-full mt-2 py-3 text-royal border border-royal rounded-full">Logout</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="block mt-2 py-3 text-center text-royal border border-royal rounded-full">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
